import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import Header from "../components/Header";
import ChatRoom from "../components/ChatRoom";
import ActiveUsers from "../components/ActiveUsers";
import toast from 'react-hot-toast';
import { UserGroupIcon, XMarkIcon, ClockIcon, PencilIcon } from '@heroicons/react/24/outline';
import { makeApiRequest } from '../utils/api';
import config from '../config';

// Get the API URL from config
const API_BASE_URL = config.apiUrl;

function Chat() {
  const [socket, setSocket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState('');
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [roomUsers, setRoomUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roomData, setRoomData] = useState(null);
  const [showUsersSidebar, setShowUsersSidebar] = useState(false);
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const typingTimeoutRef = useRef(null);
  const [showInstructions, setShowInstructions] = useState(false);

  // Function to save messages to local storage (for public rooms)
  const saveMessagesToLocalStorage = (roomId, updatedMessages) => {
    try {
      console.log(`Saving ${updatedMessages.length} messages to localStorage for room ${roomId}`);
      // Use a consistent key format that is simple and exact
      const storageKey = `chat_messages:${roomId}`;
      localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
    } catch (error) {
      console.error('Error saving messages to local storage:', error);
    }
  };
  
  // Function to get messages from local storage (for public rooms)
  const getMessagesFromLocalStorage = (roomId) => {
    try {
      // Use the same consistent key format
      const storageKey = `chat_messages:${roomId}`;
      const storedMessages = localStorage.getItem(storageKey);
      console.log(`Trying to load messages from localStorage key: ${storageKey}`);
      
      if (storedMessages) {
        const parsed = JSON.parse(storedMessages);
        console.log(`Found ${parsed.length} stored messages for room ${roomId}`);
        return parsed;
      } else {
        console.log(`No stored messages found for room ${roomId}`);
      }
    } catch (error) {
      console.error('Error loading messages from local storage:', error);
    }
    return [];
  };
  
  // Function to clear old messages from local storage
  const clearOldMessagesFromStorage = () => {
    try {
      const now = Date.now();
      const MAX_AGE = config.messageExpirationTime.public; // Use the config value for public room messages
      
      // Loop through all localStorage items
      Object.keys(localStorage).forEach(key => {
        // Only process room message keys that are NOT for the current room
        if (key.startsWith('chat_messages:') && !key.includes(roomId)) {
          try {
            const messages = JSON.parse(localStorage.getItem(key));
            
            // Make sure messages is an array
            if (Array.isArray(messages)) {
              // Filter out messages older than MAX_AGE
              const recentMessages = messages.filter(msg => {
                // Make sure timestamp exists and is valid
                if (!msg || !msg.timestamp) return false;
                
                try {
                  const msgTime = new Date(msg.timestamp).getTime();
                  return !isNaN(msgTime) && now - msgTime < MAX_AGE;
                } catch (e) {
                  console.error('Invalid timestamp in message:', msg);
                  return false;
                }
              });
              
              if (recentMessages.length === 0) {
                // Remove the entire entry if no messages remain
                localStorage.removeItem(key);
                console.log(`Cleared old messages for ${key}`);
              } else if (recentMessages.length < messages.length) {
                // Update with only recent messages
                localStorage.setItem(key, JSON.stringify(recentMessages));
                console.log(`Cleared ${messages.length - recentMessages.length} old messages from ${key}`);
              }
            } else {
              // Not a valid messages array
              console.warn(`Invalid messages format in ${key}, cleaning up`);
              localStorage.removeItem(key);
            }
          } catch (parseError) {
            console.error(`Error parsing messages in ${key}:`, parseError);
            // Clean up corrupted storage
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.error('Error clearing old messages:', error);
    }
  };

  useEffect(() => {
    const nickname = localStorage.getItem('chatNickname');
    if (!nickname) {
      navigate('/');
      return;
    }

    // If roomId exists, fetch room data
    const fetchRoomData = async () => {
      if (roomId) {
        try {
          console.log('Fetching room data...');
          const data = await makeApiRequest(`api/rooms/${roomId}`);
          
          console.log('Raw room data response:', data);
          
          // Extract room data from the response, handling different response structures
          const roomData = data.room || 
                          (data.data && data.data.room) || 
                          (data.data && data.data);
          
          if (!roomData) {
            console.error('Missing room data in server response:', data);
            throw new Error('Server response missing room data');
          }
          
          console.log('Processed room data:', roomData);
          setRoomData(roomData);
          
          // If there's a note, show it to the user
          if (data.note) {
            toast.custom((t) => (
              <div className="bg-blue-50 text-blue-500 p-4 rounded-lg shadow">
                {data.note}
              </div>
            ));
          }
        } catch (error) {
          console.error('Error fetching room:', error);
          toast.error(error.message || 'Failed to join room');
          navigate('/join-room');
          return;
        }
      }
    };

    fetchRoomData();

    // Socket connection logic
    const useMockSocket = false; // Set to false to use real sockets
    
    if (useMockSocket) {
      // Mock socket code removed
    } else {
      // Update the server URL to use environment variables
      console.log('Attempting to connect socket to:', API_BASE_URL);
      const newSocket = io(API_BASE_URL, {
        ...config.socketOptions,
        query: roomId ? { roomId } : {}
      });
    
      setSocket(newSocket);
      setUsername(nickname);

      // Join the room
      newSocket.on('connect', () => {
        console.log('Socket connected successfully to', API_BASE_URL);
        console.log('Socket connection details:', newSocket);
        console.log('Joining room', roomId);
        newSocket.emit('join', { roomId, username: nickname });
      });

      // Connection error handling
      newSocket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
        toast.error(`Connection error: ${error.message}`);
      });

      // Handle room joined confirmation
      newSocket.on('roomJoined', (data) => {
        console.log('Successfully joined room:', data);
        setIsLoading(false);
        
        // Handle different response structures
        const roomInfo = data.room || data;
        
        if (roomInfo && (roomInfo.name || roomInfo.roomName)) {
          console.log('Setting room data from socket:', roomInfo);
          setRoomData(prev => ({
            ...prev,
            ...roomInfo,
            // Ensure name is set from either property
            name: roomInfo.name || roomInfo.roomName || (prev && prev.name)
          }));
        }
      });

      // Listen for messages
      newSocket.on('message', (message) => {
        console.log('Received new message:', message);
        // Check if the message is already in the list (to prevent duplicates)
        setMessages((prev) => {
          // If message has an id, check if we already have a message with this id
          if (message.id) {
            // Check if we have this message already
            const exists = prev.some(m => m.id === message.id);
            if (exists) {
              console.log('Message already exists, not adding duplicate');
              return prev;
            }
            
            // Also check if we have a pending message with matching content that should be replaced
            const pendingIndex = prev.findIndex(m => 
              m.pending && 
              m.username === message.username && 
              m.content === message.content
            );
            
            if (pendingIndex !== -1) {
              console.log('Replacing pending message with confirmed message');
              // Replace the pending message with the confirmed one
              const updatedMessages = prev.map((m, index) => 
                index === pendingIndex ? message : m
              );
              
              // If this is a public room, save to local storage
              if (roomId && roomData && !roomData.isPrivate) {
                saveMessagesToLocalStorage(roomId, updatedMessages);
              }
              
              return updatedMessages;
            }
          }
          
          // If no matches found, add the message
          console.log('Adding new message to the list');
          const updatedMessages = [...prev, message];
          
          // If this is a public room, save to local storage
          if (roomId && roomData && !roomData.isPrivate) {
            saveMessagesToLocalStorage(roomId, updatedMessages);
          }
          
          return updatedMessages;
        });
      });

      // Listen for active users
      newSocket.on('activeUsers', (data) => {
        console.log('Received active users:', data);
        if (data && data.users) {
          setRoomUsers(data.users);
          setOnlineUsers(data.users.length);
        }
      });

      // Listen for user joined events
      newSocket.on('userJoined', (data) => {
        console.log('User joined:', data);
        // Don't show a toast notification - messages will appear in chat from server
        // for users other than the one who joined
        
        // Fetch updated user list
        newSocket.emit('getRoomUsers', roomId);
      });

      // Listen for user left events
      newSocket.on('userLeft', (data) => {
        console.log('User left:', data);
        // Don't show a toast notification - messages will appear in chat from server
        
        // Fetch updated user list
        newSocket.emit('getRoomUsers', roomId);
      });

      // Listen for typing events
      newSocket.on('userTyping', (data) => {
        if (data.username !== nickname) {
          if (data.isTyping) {
            setTypingUsers(prev => new Set([...prev, data.username]));
          } else {
            setTypingUsers(prev => {
              const newSet = new Set([...prev]);
              newSet.delete(data.username);
              return newSet;
            });
          }
        }
      });

      // Error handling
      newSocket.on('error', (error) => {
        console.error('Socket error:', error);
        toast.error(error.message || 'Connection error');
      });

    // Set up heartbeat to keep user active
    const heartbeatInterval = setInterval(() => {
      if (newSocket && newSocket.connected) {
        newSocket.emit('heartbeat');
      }
    }, 60000); // Send heartbeat every minute

    return () => {
      clearInterval(heartbeatInterval);
      if (newSocket) {
          // Properly leave the room when component unmounts
          if (roomId) {
            newSocket.emit('leaveRoom', { roomId, username: nickname });
          }
        newSocket.close();
      }
    };
    }
  }, [navigate, roomId]);

  // Add touch event handlers
  useEffect(() => {
    // Prevent bounce scrolling on iOS
    document.body.style.overflow = 'hidden';
    document.addEventListener('touchmove', (e) => {
      if (e.target.closest('.scrollable') === null) {
        e.preventDefault();
      }
    }, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', () => {});
    };
  }, []);

  // Function to fetch historical messages
  useEffect(() => {
    const fetchMessages = async () => {
      if (!roomId) return;
      
      setIsLoading(true);
      console.log('Fetching messages for room:', roomId);
      
      // First, always check localStorage for any messages
      const localMessages = getMessagesFromLocalStorage(roomId);
      if (localMessages && localMessages.length > 0) {
        console.log(`Found ${localMessages.length} messages in localStorage, setting these immediately`);
        // Set messages from localStorage immediately to avoid flashing empty chat
        setMessages(localMessages);
      }
      
      try {
        const data = await makeApiRequest(`api/messages/${roomId}`);
        console.log('API response:', data);
        
        // Extract room info from the response
        const roomInfo = data.room || 
                         (data.data && data.data.room) || 
                         (data.data && data.data);
        
        if (roomInfo) {
          console.log('Room info:', roomInfo);
          console.log('Room type:', roomInfo.isPrivate ? 'private' : 'public');
          setRoomData(roomInfo);
          
          if (roomInfo.activeUsers !== undefined) {
            setOnlineUsers(roomInfo.activeUsers);
          }
          
          // Get messages from the database response
          const messagesData = data.messages || 
                              (data.data && data.data.messages) || 
                              [];
          
          console.log(`API returned ${messagesData.length} messages`);
          
          if (messagesData && Array.isArray(messagesData) && messagesData.length > 0) {
            console.log(`Setting ${messagesData.length} messages from API response`);
            setMessages(messagesData);
            
            // Always save API messages to localStorage for persistence
            console.log(`Saving ${messagesData.length} messages to localStorage`);
            saveMessagesToLocalStorage(roomId, messagesData);
          } else if (localMessages && localMessages.length > 0) {
            // If API returned no messages but we have local messages, keep using those
            console.log(`API returned no messages, keeping ${localMessages.length} messages from localStorage`);
            // We already set these above, so no need to setMessages again
          } else {
            console.log('No messages found in API response or localStorage');
            setMessages([]);
          }
        } else {
          console.error('No room info in response:', data);
          // If we have local messages, keep using those
          if (localMessages && localMessages.length > 0) {
            console.log(`No room info but keeping ${localMessages.length} local messages`);
            // We already set these above, so no need to setMessages again
          } else {
            setMessages([]);
          }
        }
      } catch (error) {
        console.error('Error fetching room info:', error);
        toast.error('Failed to load chat room data. Using locally stored messages.');
        
        // We already loaded local messages above, so no need to do it again here
      } finally {
        setIsLoading(false);
      }
    };

    if (socket && socket.connected) {
      fetchMessages();
      // Only run cleanup for other rooms, not the current one
      clearOldMessagesFromStorage();
    }
  }, [roomId, socket]);

  // Listen for messages
  useEffect(() => {
    if (!socket) return;
    
    const messageHandler = (message) => {
      console.log('Received message from socket:', message);
      
      // Add message to state and localStorage in one operation to ensure consistency
      setMessages((prev) => {
        let updatedMessages = [...prev];
        let changed = false;
        
        // If the message has an ID, check for duplicates or pending messages to replace
        if (message.id) {
          // Check if we already have this message
          const existingIndex = prev.findIndex(m => m.id === message.id);
          if (existingIndex !== -1) {
            console.log('Message already exists, not adding duplicate');
            return prev; // No change needed
          }
          
          // Check if we have a pending message with matching content that should be replaced
          const pendingIndex = prev.findIndex(m => 
            m.pending && 
            m.username === message.username && 
            m.content === message.content
          );
          
          if (pendingIndex !== -1) {
            console.log('Replacing pending message with confirmed message');
            // Replace the pending message with the confirmed one
            updatedMessages = prev.map((m, index) => 
              index === pendingIndex ? message : m
            );
            changed = true;
          } else {
            // No pending message to replace, add as new
            console.log('Adding new message with ID to messages list');
            updatedMessages = [...prev, message];
            changed = true;
          }
        } else {
          // Message has no ID, just add it (this shouldn't usually happen)
          console.log('Adding message without ID to messages list');
          updatedMessages = [...prev, message];
          changed = true;
        }
        
        // If we made any changes, save to localStorage
        if (changed && roomId) {
          console.log(`Saving ${updatedMessages.length} messages to localStorage from socket message`);
          saveMessagesToLocalStorage(roomId, updatedMessages);
        }
        
        return changed ? updatedMessages : prev;
      });
    };
    
    socket.on('message', messageHandler);
    
    return () => {
      socket.off('message', messageHandler);
    };
  }, [socket, roomId]);

  // Handler for typing events
  const handleTyping = (isTyping) => {
    if (!socket || !socket.connected || !roomId) return;
    
    // Only emit if state changed
    if (isTyping !== isTyping) {
      setIsTyping(isTyping);
      socket.emit('typing', { roomId, username, isTyping });
      
      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // If typing, set timeout to clear typing status after 2 seconds
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false);
          socket.emit('typing', { roomId, username, isTyping: false });
        }, 2000);
      }
    }
  };

  // Send a message
  const sendMessage = (messageText) => {
    if (!messageText.trim() || !socket || !socket.connected) return;
    
    // Clear typing status
    handleTyping(false);
    
    // Create a temporary message for immediate display with a unique ID
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      roomId,
      username,
      content: messageText,
      timestamp: new Date().toISOString(),
      pending: true 
    };
    
    // Update UI immediately for responsive feeling
    setMessages(prev => {
      const updatedMessages = [...prev, tempMessage];
      
      // Save to localStorage regardless of room type
      console.log(`Saving message temporarily with ID ${tempId}`);
      saveMessagesToLocalStorage(roomId, updatedMessages);
      
      return updatedMessages;
    });
    
    // Send message via socket
    console.log('Sending message to server:', messageText);
    socket.emit('sendMessage', { 
      roomId, 
      username, 
      content: messageText 
    });
    
    // Set up a timeout to check if message was acknowledged
    setTimeout(() => {
      setMessages(prev => {
        // If the temp message is still there with pending status
        const messageStillPending = prev.find(msg => msg.id === tempId && msg.pending);
        if (messageStillPending) {
          console.log(`Message ${tempId} still pending after timeout, marking as error`);
          // Replace it with error status but keep it visible
          const updatedMessages = prev.map(msg => 
            msg.id === tempId 
              ? {...msg, pending: false, error: true}
              : msg
          );
          
          // Save to localStorage regardless of room type
          saveMessagesToLocalStorage(roomId, updatedMessages);
          
          return updatedMessages;
        }
        return prev;
      });
    }, 5000); // 5 second timeout
  };

  const toggleUsersSidebar = () => {
    setShowUsersSidebar(prev => !prev);
  };

  // Add an InstructionCard component
  const InstructionCard = ({ room, onClose }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md shadow-xl border border-gray-700 animate-bounce-in">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-semibold text-blue-400 flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5" />
            Welcome to {room?.name || 'the chat room'}!
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-gray-300 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        
        <div className="space-y-4 mb-6">
          <p className="text-gray-300">
            This is a real-time chat room where all messages are visible to everyone in the room.
          </p>
          
          <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
            <h4 className="font-medium text-blue-300 mb-2">Important Information</h4>
            <ul className="space-y-2 text-sm text-gray-300">
              <li className="flex items-start">
                <ClockIcon className="h-5 w-5 mr-2 flex-shrink-0 text-blue-400" />
                <span>
                  {room?.isPrivate 
                    ? "Messages in this private room will be stored for 7 days."
                    : "Messages in this public room will be stored locally for 1 hour."}
                </span>
              </li>
              <li className="flex items-start">
                <UserGroupIcon className="h-5 w-5 mr-2 flex-shrink-0 text-blue-400" />
                <span>You can see who's currently active in the room in the sidebar.</span>
              </li>
              <li className="flex items-start">
                <PencilIcon className="h-5 w-5 mr-2 flex-shrink-0 text-blue-400" />
                <span>Others can see when you're typing a message.</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="text-right">
          <button
            onClick={onClose}
            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="text-gray-400">Connecting to chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <Header 
        username={username} 
        onlineUsers={onlineUsers} 
        socket={socket}
        roomName={roomData?.name}
        onToggleUsers={() => setShowUsersSidebar(!showUsersSidebar)}
      />

      {/* Debugging controls - remove in production */}
      <div className="bg-gray-800 p-2 text-xs flex space-x-2 items-center justify-center">
        <button 
          onClick={() => {
            const messages = getMessagesFromLocalStorage(roomId);
            console.log(`DEBUG: Found ${messages?.length || 0} messages in localStorage`, messages);
            toast.success(`Found ${messages?.length || 0} messages in localStorage`);
          }}
          className="px-2 py-1 bg-blue-600 text-white rounded text-xs"
        >
          Check localStorage
        </button>
        <button 
          onClick={() => {
            const testMsg = {
              id: `test-${Date.now()}`,
              roomId,
              username: "Test User",
              content: `Test message at ${new Date().toLocaleTimeString()}`,
              timestamp: new Date().toISOString(),
              type: 'message'
            };
            setMessages(prev => {
              const updated = [...prev, testMsg];
              saveMessagesToLocalStorage(roomId, updated);
              return updated;
            });
            toast.success("Added test message to localStorage");
          }}
          className="px-2 py-1 bg-green-600 text-white rounded text-xs"
        >
          Add test message
        </button>
        <button 
          onClick={() => {
            localStorage.removeItem(`chat_messages:${roomId}`);
            setMessages([]);
            toast.success("Cleared localStorage messages");
          }}
          className="px-2 py-1 bg-red-600 text-white rounded text-xs"
        >
          Clear messages
        </button>
      </div>

      {/* Main content area with chat room and sidebar */}
      <div className="flex-1 flex relative">
        {/* Chat content */}
        <div className="flex-1 overflow-hidden relative">
          {isLoading ? (
            // Loading state
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-4">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-gray-400">Connecting to chat room...</p>
              </div>
            </div>
          ) : (
          <ChatRoom 
            messages={messages}
            username={username}
            onSendMessage={sendMessage}
              roomName={roomData?.name}
              onTyping={handleTyping}
            />
          )}

          {/* Instruction card - shown initially */}
          {showInstructions && roomData && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-30" onClick={() => setShowInstructions(false)}>
              <div className="bg-gray-800 max-w-md mx-auto rounded-xl shadow-2xl p-6 m-4 border border-gray-700" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-900/50 rounded-full">
                      <UserGroupIcon className="h-6 w-6 text-blue-400" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Welcome to {roomData.name}</h3>
                  </div>
                  <button 
                    onClick={() => setShowInstructions(false)} 
                    className="text-gray-400 hover:text-white p-1"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-4 text-gray-300">
                  <p className="border-l-2 border-blue-500 pl-3 py-1">
                    You've joined a <span className="font-semibold">{roomData.isPrivate ? 'private' : 'public'}</span> chat room.
                  </p>
                  
                  <div className="rounded-lg bg-gray-900/50 p-4 text-sm space-y-3">
                    <div className="flex items-start">
                      <ClockIcon className="h-5 w-5 mr-2 text-blue-400 shrink-0 mt-0.5" /> 
                      <span>
                        Messages are stored for <span className="font-medium text-white">{roomData.isPrivate ? '7 days' : '1 day'}</span> in {roomData.isPrivate ? 'private' : 'public'} rooms.
                      </span>
                    </div>
                    
                    <div className="flex items-start">
                      <PencilIcon className="h-5 w-5 mr-2 text-blue-400 shrink-0 mt-0.5" />
                      <span>
                        As you type, other users will see a typing indicator.
                      </span>
                    </div>
                  </div>
                  
                  <button 
                    onClick={() => setShowInstructions(false)}
                    className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors mt-2"
                  >
                    Got it
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Users sidebar - conditionally rendered based on state */}
        <div className={`bg-gray-900 border-l border-gray-700 w-64 absolute top-0 bottom-0 right-0 transform transition-transform duration-300 ease-in-out z-20 md:static ${showUsersSidebar ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}`}>
          {/* Close button for mobile view */}
          <button 
            onClick={() => setShowUsersSidebar(false)}
            className="md:hidden absolute top-2 right-2 text-gray-500 hover:text-white p-1.5 rounded-full hover:bg-gray-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
          
          {/* Render ActiveUsers component */}
          <ActiveUsers users={roomUsers} orientation="vertical" />
          </div>
      </div>
    </div>
  );
}

export default Chat; 